import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';
import { getGA4GeoCity } from '../../../api/auth';
import { DriveMarketMap } from '../ChartsSection';
import { fmtNumber, fmtPercent } from '../shared/format';
import {
  sortRows, weightedAvg, downloadCsv, downloadPdf, makeExport,
  CHANNEL_COLORS,
  SortableHeader, PaginationFooter, TableSkeleton, SectionInsightCard, GA4DimensionNote, ViewToggle,
} from '../shared';
import ThemedChartTooltip from '../../shared/ThemedChartTooltip';
import ThinScroll from '../../shared/ThinScroll';
import { SkeletonChart, SkeletonText } from '../../shared/Skeleton';
import { ErrorState, EmptyState } from '../../shared/StateMessage';
import useTablePage from '../../../hooks/useTablePage';
import { unwrapItems } from '../../shared/unwrapItems';
import { expandableRowProps } from '../../shared/expandableRow';

/* ============================= Geo: Region table (with country toggle + city drill-down) ============================= */
const COUNTRY_FILTERS = [
  { id: 'us', label: 'US', match: country => /^(united states|us|usa)$/i.test(country) },
  { id: 'ca', label: 'Canada', match: country => /^(canada|ca)$/i.test(country) },
  { id: 'all', label: 'All', match: () => true },
];

const GeoRegionTable = ({ data, loading, error, searchQuery = '', clientId, selectedGeoWindow }) => {
  const [sort, setSort] = useState({ key: 'sessions', dir: 'desc' });
  const [countryFilter, setCountryFilter] = useState('us');
  const perPage = 10;
  const [expandedRegion, setExpandedRegion] = useState(null);
  const [cityData, setCityData] = useState({});
  const [cityLoading, setCityLoading] = useState({});
  const [cityPage, setCityPage] = useState({});
  const cityPerPage = 10;

  // Clients are US/Canada-based, so 'us' is the right default. But for a client with
  // no US traffic this period it would show an empty table — so when fresh data lands,
  // if the US filter matches nothing, fall back to 'all'. Guarded on the data reference
  // so it runs once per dataset and doesn't override a later manual filter choice.
  const autoFilterRef = useRef(null);
  useEffect(() => {
    if (!data?.length || autoFilterRef.current === data) return;
    autoFilterRef.current = data;
    const usFilter = COUNTRY_FILTERS.find(filterDef => filterDef.id === 'us');
    if (!data.some(row => usFilter.match(row.country || ''))) {
      setCountryFilter('all');
    }
  }, [data]);

  const handleRegionToggle = useCallback(async (row, regionKey) => {
    const key = regionKey;
    if (expandedRegion === key) {
      setExpandedRegion(null);
      return;
    }
    setExpandedRegion(key);
    if (cityData[key]) return;
    if (!clientId || !selectedGeoWindow) return;
    setCityLoading(prev => ({ ...prev, [key]: true }));
    try {
      const resp = await getGA4GeoCity(clientId, selectedGeoWindow.date_start, selectedGeoWindow.date_end, row.country, row.region);
      // fn_get_ga4_geo_region returns an { items, level, window } envelope at the
      // city level too — unwrap `.items` so the drill-down rows render.
      setCityData(prev => ({ ...prev, [key]: unwrapItems(resp) }));
    } catch {
      setCityData(prev => ({ ...prev, [key]: [] }));
    } finally {
      setCityLoading(prev => ({ ...prev, [key]: false }));
    }
  }, [expandedRegion, clientId, selectedGeoWindow, cityData]);

  const handleSort = (key) => {
    setSort((prev) => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' });
  };

  const filtered = useMemo(() => {
    if (!data) return [];
    const countryFilterDef = COUNTRY_FILTERS.find(filterDef => filterDef.id === countryFilter) || COUNTRY_FILTERS[2];
    let rows = data.filter(row => countryFilterDef.match(row.country || ''));
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      rows = rows.filter(row => (row.region || '').toLowerCase().includes(query) || (row.country || '').toLowerCase().includes(query));
    }
    return rows;
  }, [data, countryFilter, searchQuery]);

  const showCountryTag = countryFilter === 'all';
  const regions = useMemo(() => {
    const grouped = {};
    for (const row of filtered) {
      const region = row.region || 'Unknown';
      const country = row.country || '';
      const key = showCountryTag ? `${country}|||${region}` : region;
      if (!grouped[key]) {
        grouped[key] = { region, country, sessions: 0, users: 0, new_users: 0, bounceNum: 0, bounceDen: 0, conversions: 0 };
      }
      grouped[key].sessions += row.sessions || 0;
      grouped[key].users += row.users || 0;
      grouped[key].new_users += row.new_users || 0;
      grouped[key].conversions += row.conversions || 0;
      if (row.bounce_rate != null && row.sessions) {
        grouped[key].bounceNum += row.bounce_rate * row.sessions;
        grouped[key].bounceDen += row.sessions;
      }
    }
    return Object.values(grouped).map(group => ({
      ...group,
      bounce_rate: group.bounceDen > 0 ? group.bounceNum / group.bounceDen : null,
      pct_of_total: 0,
    }));
  }, [filtered, showCountryTag]);

  const totalSessions = regions.reduce((sum, region) => sum + region.sessions, 0);
  const enriched = regions.map(region => ({ ...region, pct_of_total: totalSessions > 0 ? (region.sessions / totalSessions) * 100 : 0 }));
  const sorted = sortRows(enriched, sort.key, sort.dir);

  // Must run before the early returns below — hooks can't be called conditionally.
  // `countryFilter` narrows the rows too, so it's a reset trigger alongside data/search/sort.
  const { page, setPage, totalPages, sliceStart, sliceEnd } = useTablePage(sorted.length, perPage, [data, searchQuery, sort, countryFilter]);

  if (error) return <ErrorState message={error} />;
  if (loading) return <TableSkeleton />;
  if (!data || data.length === 0) {
    return <EmptyState message="No geo data for this period." padding="py-12" />;
  }

  const totals = {
    sessions: totalSessions,
    users: enriched.reduce((sum, region) => sum + region.users, 0),
    new_users: enriched.reduce((sum, region) => sum + region.new_users, 0),
    bounce_rate: weightedAvg(enriched, 'bounce_rate', 'sessions'),
    conversions: enriched.reduce((sum, region) => sum + region.conversions, 0),
  };

  return (
    <div>
      <div className="flex items-center gap-1.5 mb-4">
        {COUNTRY_FILTERS.map((filterDef) => (
          <button
            key={filterDef.id}
            type="button"
            onClick={() => { setCountryFilter(filterDef.id); setPage(1); setExpandedRegion(null); }}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${
              countryFilter === filterDef.id
                ? 'bg-[#32B1FF]/10 text-[#32B1FF] border border-[#32B1FF]/30'
                : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 border border-transparent'
            }`}
          >
            {filterDef.label}
          </button>
        ))}
      </div>
      <ThinScroll className="responsive-card-table-wrapper bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-x-auto">
        <table className="responsive-card-table w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
              <SortableHeader label="State / Region" sortKey="region" currentSort={sort} onSort={handleSort} className="min-w-[200px]" />
              <SortableHeader label="Sessions" sortKey="sessions" currentSort={sort} onSort={handleSort} />
              <SortableHeader label="% of Total" sortKey="pct_of_total" currentSort={sort} onSort={handleSort} />
              <SortableHeader label="Users" sortKey="users" currentSort={sort} onSort={handleSort} />
              <SortableHeader label="New Users" sortKey="new_users" currentSort={sort} onSort={handleSort} />
              <SortableHeader label="Bounce Rate" sortKey="bounce_rate" currentSort={sort} onSort={handleSort} />
              <SortableHeader label="Conversions" sortKey="conversions" currentSort={sort} onSort={handleSort} />
            </tr>
          </thead>
          <tbody>
            {sorted.slice(sliceStart, sliceEnd).map((row) => {
              const regionKey = showCountryTag ? `${row.country}|||${row.region}` : row.region;
              const isExpanded = expandedRegion === regionKey;
              const cities = cityData[regionKey];
              const citiesLoading = cityLoading[regionKey];
              return (
                <React.Fragment key={regionKey}>
                  <tr
                    {...expandableRowProps({
                      expanded: isExpanded,
                      onToggle: () => handleRegionToggle(row, regionKey),
                      label: `city breakdown for ${row.region}`,
                      className: `border-b dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition cursor-pointer ${isExpanded ? '' : 'rc-collapsed'}`,
                    })}
                  >
                    <td data-label="State / Region" className={`rc-title-cell px-3 py-2.5 text-gray-900 dark:text-gray-100 font-medium ${isExpanded ? 'rc-title-open' : ''}`}>
                      <span className="flex items-center gap-1.5">
                        <svg className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 5l7 7-7 7" /></svg>
                        {row.region}
                        {showCountryTag && row.country && (
                          <span className="ml-1 px-1.5 py-0.5 text-[10px] font-medium rounded bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                            {row.country}
                          </span>
                        )}
                      </span>
                    </td>
                    <td data-label="Sessions" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtNumber(row.sessions)}</td>
                    <td data-label="% of Total" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{row.pct_of_total.toFixed(1)}%</td>
                    <td data-label="Users" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtNumber(row.users)}</td>
                    <td data-label="New Users" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtNumber(row.new_users)}</td>
                    <td data-label="Bounce Rate" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtPercent(row.bounce_rate)}</td>
                    <td data-label="Conversions" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtNumber(row.conversions)}</td>
                  </tr>
                  {isExpanded && (
                    citiesLoading ? (
                      <tr className="bg-gray-50/50 dark:bg-gray-800/30">
                        <td colSpan={7} className="px-3 py-4">
                          <SkeletonText lines={4} />
                        </td>
                      </tr>
                    ) : cities && cities.length > 0 ? (
                      <>
                        {(() => {
                          const currentCityPage = cityPage[regionKey] || 1;
                          const totalCityPages = Math.ceil(cities.length / cityPerPage);
                          const paginatedCities = cities.slice((currentCityPage - 1) * cityPerPage, currentCityPage * cityPerPage);
                          return (
                            <>
                              {paginatedCities.map((city, cityIndex) => (
                                <tr key={`${regionKey}-city-${cityIndex}`} className="bg-gray-50/50 dark:bg-gray-800/30 border-b dark:border-gray-800/50">
                                  <td data-label="City" className="px-3 py-2 text-gray-600 dark:text-gray-400 text-xs pl-10 max-[499px]:!pl-3">{city.city || '(not set)'}</td>
                                  <td data-label="Sessions" className="px-3 py-2 text-gray-600 dark:text-gray-400 text-xs">{fmtNumber(city.sessions)}</td>
                                  <td data-label="% of Region" className="px-3 py-2 text-gray-600 dark:text-gray-400 text-xs">
                                    {row.sessions > 0 ? ((city.sessions / row.sessions) * 100).toFixed(1) : '0.0'}%
                                  </td>
                                  <td data-label="Users" className="px-3 py-2 text-gray-600 dark:text-gray-400 text-xs">{fmtNumber(city.users)}</td>
                                  <td data-label="New Users" className="px-3 py-2 text-gray-600 dark:text-gray-400 text-xs">{fmtNumber(city.new_users)}</td>
                                  <td data-label="Bounce Rate" className="px-3 py-2 text-gray-600 dark:text-gray-400 text-xs">{fmtPercent(city.bounce_rate)}</td>
                                  <td data-label="Conversions" className="px-3 py-2 text-gray-600 dark:text-gray-400 text-xs">{fmtNumber(city.conversions)}</td>
                                </tr>
                              ))}
                              {totalCityPages > 1 && (
                                <tr className="bg-gray-50/50 dark:bg-gray-800/30">
                                  <td colSpan={7} className="px-3 py-1" onClick={(event) => event.stopPropagation()}>
                                    <PaginationFooter
                                      pagination={{ total_items: cities.length, total_pages: totalCityPages }}
                                      page={currentCityPage}
                                      perPage={cityPerPage}
                                      onPageChange={(nextPage) => setCityPage(prev => ({ ...prev, [regionKey]: nextPage }))}
                                      label="cities"
                                    />
                                  </td>
                                </tr>
                              )}
                            </>
                          );
                        })()}
                      </>
                    ) : cities ? (
                      <tr className="bg-gray-50/50 dark:bg-gray-800/30">
                        <td colSpan={7} className="px-3 py-3 text-center text-xs text-gray-500 dark:text-gray-400">No city data for this region.</td>
                      </tr>
                    ) : null
                  )}
                </React.Fragment>
              );
            })}
            <tr className="bg-gray-50 dark:bg-gray-800/70 font-semibold">
              <td data-label="Total" className="px-3 py-2.5 text-gray-900 dark:text-gray-100">Total ({enriched.length} regions)</td>
              <td data-label="Sessions" className="px-3 py-2.5 text-gray-900 dark:text-gray-100">{fmtNumber(totals.sessions)}</td>
              <td data-label="% of Total" className="px-3 py-2.5 text-gray-900 dark:text-gray-100">100%</td>
              <td data-label="Users" className="px-3 py-2.5 text-gray-900 dark:text-gray-100">{fmtNumber(totals.users)}</td>
              <td data-label="New Users" className="px-3 py-2.5 text-gray-900 dark:text-gray-100">{fmtNumber(totals.new_users)}</td>
              <td data-label="Bounce Rate" className="px-3 py-2.5 text-gray-900 dark:text-gray-100">{fmtPercent(totals.bounce_rate)}</td>
              <td data-label="Conversions" className="px-3 py-2.5 text-gray-900 dark:text-gray-100">{fmtNumber(totals.conversions)}</td>
            </tr>
          </tbody>
        </table>
      </ThinScroll>
      <PaginationFooter
        pagination={{ total_items: sorted.length, total_pages: Math.ceil(sorted.length / perPage) }}
        page={page}
        perPage={perPage}
        onPageChange={setPage}
        label="regions"
      />
    </div>
  );
};

/* ============================= Geo: Country table ============================= */
const GeoCountryTable = ({ data, loading, error, searchQuery = '' }) => {
  const [sort, setSort] = useState({ key: 'sessions', dir: 'desc' });
  const perPage = 10;

  const handleSort = (key) => {
    setSort((prev) => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' });
  };

  const countries = useMemo(() => {
    if (!data) return [];
    let rows = data;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      rows = rows.filter(row => (row.country || '').toLowerCase().includes(query));
    }
    const grouped = {};
    for (const row of rows) {
      const country = row.country || 'Unknown';
      if (!grouped[country]) {
        grouped[country] = { country, sessions: 0, users: 0, new_users: 0, bounceNum: 0, bounceDen: 0, conversions: 0 };
      }
      grouped[country].sessions += row.sessions || 0;
      grouped[country].users += row.users || 0;
      grouped[country].new_users += row.new_users || 0;
      grouped[country].conversions += row.conversions || 0;
      if (row.bounce_rate != null && row.sessions) {
        grouped[country].bounceNum += row.bounce_rate * row.sessions;
        grouped[country].bounceDen += row.sessions;
      }
    }
    return Object.values(grouped).map(group => ({ ...group, bounce_rate: group.bounceDen > 0 ? group.bounceNum / group.bounceDen : null }));
  }, [data, searchQuery]);

  const sorted = useMemo(() => sortRows(countries, sort.key, sort.dir), [countries, sort]);
  const totalItems = sorted.length;
  const { page, setPage, totalPages, sliceStart, sliceEnd } = useTablePage(totalItems, perPage, [data, searchQuery, sort]);
  const paged = sorted.slice(sliceStart, sliceEnd);

  if (error) return <ErrorState message={error} />;
  if (loading) return <TableSkeleton />;
  if (!data || data.length === 0) {
    return <EmptyState message="No geo data for this period." padding="py-12" />;
  }

  const totals = {
    sessions: countries.reduce((sum, country) => sum + country.sessions, 0),
    users: countries.reduce((sum, country) => sum + country.users, 0),
    new_users: countries.reduce((sum, country) => sum + country.new_users, 0),
    bounce_rate: weightedAvg(countries, 'bounce_rate', 'sessions'),
    conversions: countries.reduce((sum, country) => sum + country.conversions, 0),
  };

  return (
    <div>
      <ThinScroll className="responsive-card-table-wrapper bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-x-auto">
        <table className="responsive-card-table w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
              <SortableHeader label="Country" sortKey="country" currentSort={sort} onSort={handleSort} className="min-w-[200px]" />
              <SortableHeader label="Sessions" sortKey="sessions" currentSort={sort} onSort={handleSort} />
              <SortableHeader label="Users" sortKey="users" currentSort={sort} onSort={handleSort} />
              <SortableHeader label="New Users" sortKey="new_users" currentSort={sort} onSort={handleSort} />
              <SortableHeader label="Bounce Rate" sortKey="bounce_rate" currentSort={sort} onSort={handleSort} />
              <SortableHeader label="Conversions" sortKey="conversions" currentSort={sort} onSort={handleSort} />
            </tr>
          </thead>
          <tbody>
            {paged.map((row) => (
              <tr key={row.country} className="border-b dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition">
                <td data-label="Country" className="px-3 py-2.5 text-gray-900 dark:text-gray-100 font-medium">{row.country}</td>
                <td data-label="Sessions" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtNumber(row.sessions)}</td>
                <td data-label="Users" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtNumber(row.users)}</td>
                <td data-label="New Users" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtNumber(row.new_users)}</td>
                <td data-label="Bounce Rate" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtPercent(row.bounce_rate)}</td>
                <td data-label="Conversions" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtNumber(row.conversions)}</td>
              </tr>
            ))}
            <tr className="bg-gray-50 dark:bg-gray-800/70 font-semibold">
              <td data-label="Total" className="px-3 py-2.5 text-gray-900 dark:text-gray-100">Total ({totalItems} countries)</td>
              <td data-label="Sessions" className="px-3 py-2.5 text-gray-900 dark:text-gray-100">{fmtNumber(totals.sessions)}</td>
              <td data-label="Users" className="px-3 py-2.5 text-gray-900 dark:text-gray-100">{fmtNumber(totals.users)}</td>
              <td data-label="New Users" className="px-3 py-2.5 text-gray-900 dark:text-gray-100">{fmtNumber(totals.new_users)}</td>
              <td data-label="Bounce Rate" className="px-3 py-2.5 text-gray-900 dark:text-gray-100">{fmtPercent(totals.bounce_rate)}</td>
              <td data-label="Conversions" className="px-3 py-2.5 text-gray-900 dark:text-gray-100">{fmtNumber(totals.conversions)}</td>
            </tr>
          </tbody>
        </table>
      </ThinScroll>
      <PaginationFooter pagination={{ total_items: totalItems, total_pages: totalPages }} page={page} perPage={perPage} onPageChange={setPage} label="countries" />
    </div>
  );
};

/* ============================= Geo trend (multi-window) ============================= */
const GeoTrendChart = ({ geoWindows, geoTrendData, geoTrendLoading, error }) => {
  const windowData = geoTrendData;
  const loading = geoTrendLoading;

  const { topRegions, chartData } = useMemo(() => {
    if (!windowData || windowData.length === 0) return { topRegions: [], chartData: [] };

    const totals = {};
    for (const windowDatum of windowData) {
      for (const row of windowDatum.data) {
        const regionName = row.region || row.country || '(unknown)';
        totals[regionName] = (totals[regionName] || 0) + (row.sessions || 0);
      }
    }
    const top = Object.entries(totals)
      .sort((entryA, entryB) => entryB[1] - entryA[1])
      .slice(0, 5)
      .map(([regionName]) => regionName);

    const cd = windowData
      .sort((windowA, windowB) => windowA.window.date_start.localeCompare(windowB.window.date_start))
      .map((windowDatum) => {
        const point = {
          label: new Date(windowDatum.window.date_start + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        };
        for (const region of top) {
          const match = windowDatum.data.find((row) => (row.region || row.country) === region);
          point[region] = match?.sessions || 0;
        }
        return point;
      });

    return { topRegions: top, chartData: cd };
  }, [windowData]);

  if (error) return <ErrorState message={error} />;

  if (!geoWindows || geoWindows.length < 2) {
    return (
      <EmptyState message="Trend data will be available once you have multiple weeks of analytics data." padding="py-12" />
    );
  }

  if (loading) {
    return <SkeletonChart height={280} />;
  }

  if (!windowData || chartData.length < 2 || topRegions.length === 0) {
    return <EmptyState message="Not enough geo data for trends." padding="py-12" />;
  }

  return (
    <div className="rotate-x-labels">
      <ResponsiveContainer width="100%" height={320}>
        <LineChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="text-gray-200 dark:text-gray-700" />
          <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#9CA3AF' }} />
          <YAxis
            tick={{ fontSize: 11, fill: '#9CA3AF' }}
            tickFormatter={(value) => value >= 1000 ? `${(value / 1000).toFixed(1)}k` : value}
            width={50}
          />
          <ThemedChartTooltip
            formatter={(value, name) => [Number(value).toLocaleString(), name]}
          />
          {topRegions.map((region, index) => (
            <Line
              key={region}
              type="monotone"
              dataKey={region}
              name={region}
              stroke={CHANNEL_COLORS[index % CHANNEL_COLORS.length]}
              strokeWidth={2}
              dot={{ r: 4 }}
              activeDot={{ r: 5, strokeWidth: 2 }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>

      <div className="flex flex-wrap items-center gap-3 mt-3 px-2">
        {topRegions.map((region, index) => (
          <div key={region} className="flex items-center gap-1.5">
            <span className="w-3 h-0.5 rounded-full inline-block" style={{ backgroundColor: CHANNEL_COLORS[index % CHANNEL_COLORS.length] }} />
            <span className="text-xs text-gray-600 dark:text-gray-400">{region}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ============================= Region / Country sub-tab container ============================= */
const GeoTabContent = ({ data, loading, error, searchQuery, clientId, selectedGeoWindow }) => {
  const [subTab, setSubTab] = useState('region');

  const subTabs = [
    { id: 'region', label: 'Region' },
    { id: 'country', label: 'Country' },
  ];

  return (
    <div>
      <div className="flex items-center gap-1.5 mb-4">
        {subTabs.map((subTabItem) => (
          <button
            key={subTabItem.id}
            type="button"
            onClick={() => setSubTab(subTabItem.id)}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${
              subTab === subTabItem.id
                ? 'bg-[#32B1FF]/10 text-[#32B1FF] border border-[#32B1FF]/30'
                : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 border border-transparent'
            }`}
          >
            {subTabItem.label}
          </button>
        ))}
        <GA4DimensionNote className="ml-2" />
      </div>

      {subTab === 'region' && (
        <GeoRegionTable data={data} loading={loading} error={error} searchQuery={searchQuery} clientId={clientId} selectedGeoWindow={selectedGeoWindow} />
      )}
      {subTab === 'country' && (
        <GeoCountryTable data={data} loading={loading} error={error} searchQuery={searchQuery} />
      )}
    </div>
  );
};

/* ============================= Insight summary ============================= */
export const getGeoSummary = (data) => {
  if (!data || data.length === 0) return { summary: [], warnings: [] };
  const summary = [];
  const byRegion = {};
  for (const row of data) {
    const region = row.region || 'Unknown';
    byRegion[region] = (byRegion[region] || 0) + (row.sessions || 0);
  }
  const sorted = Object.entries(byRegion).sort((entryA, entryB) => entryB[1] - entryA[1]);
  const total = sorted.reduce((sum, [, sessions]) => sum + sessions, 0);
  if (sorted.length >= 3 && total > 0) {
    const top3 = sorted.slice(0, 3);
    const top3Pct = ((top3.reduce((sum, [, sessions]) => sum + sessions, 0) / total) * 100).toFixed(1);
    summary.push(`Top 3 regions (${top3.map(([region]) => region).join(', ')}) account for ${top3Pct}% of traffic.`);
  }
  return { summary, warnings: [] };
};

/* ============================= Export helpers ============================= */
// One column spec drives CSV + PDF (can't drift). CSV emits raw values + full
// headers; PDF emits formatted strings + abbreviated headers.
const GEO_EXPORT = makeExport([
  { header: 'Region', get: (row) => row.region },
  { header: 'Country', get: (row) => row.country },
  { header: 'Sessions', get: (row) => row.sessions, pdf: (row) => fmtNumber(row.sessions) },
  { header: 'Users', get: (row) => row.users, pdf: (row) => fmtNumber(row.users) },
  { header: 'New Users', get: (row) => row.new_users, pdf: (row) => fmtNumber(row.new_users) },
  { header: 'Bounce Rate', pdfHeader: 'Bounce', get: (row) => (row.bounce_rate != null ? (row.bounce_rate * 100).toFixed(1) + '%' : ''), pdf: (row) => fmtPercent(row.bounce_rate) },
  { header: 'Conversions', pdfHeader: 'Conv.', get: (row) => row.conversions, pdf: (row) => fmtNumber(row.conversions) },
]);

export const exportGeoCsv = (geoData, rangeStart, rangeEnd) => {
  if (!geoData?.length) return;
  downloadCsv(GEO_EXPORT.headers, geoData.map(GEO_EXPORT.toCsvRow), `ga4-geo-${rangeStart}-to-${rangeEnd}.csv`);
};

export const exportGeoPdf = (geoData, rangeStart, rangeEnd) => {
  if (!geoData?.length) return;
  const dateRange = rangeStart && rangeEnd ? `${rangeStart} to ${rangeEnd}` : '';
  downloadPdf('Geography', GEO_EXPORT.pdfHeaders, geoData.map(GEO_EXPORT.toPdfRow), `ga4-geo-${rangeStart}-to-${rangeEnd}.pdf`, dateRange);
};

/* ============================= Tab component ============================= */

const GeoTab = ({ geoData, geoLoading, geoError, geoWindows, geoTrendData, geoTrendLoading, selectedGeoWindow, searchQuery, selectedClientId }) => {
  const [view, setView] = useState('table');

  return (
    <div>
      <SectionInsightCard title="Geo Insight" {...getGeoSummary(geoData)} />
      <ViewToggle view={view} onChange={setView} />
      {view === 'table' ? (
        <>
          <div className="mb-6">
            <DriveMarketMap data={geoData} loading={geoLoading} error={geoError} />
          </div>
          <GeoTabContent data={geoData} loading={geoLoading} error={geoError} searchQuery={searchQuery} clientId={selectedClientId} selectedGeoWindow={selectedGeoWindow} />
        </>
      ) : (
        <GeoTrendChart geoWindows={geoWindows} geoTrendData={geoTrendData} geoTrendLoading={geoTrendLoading} error={geoError} />
      )}
    </div>
  );
};

export default GeoTab;
